'use client';

import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { usePathname } from 'next/navigation';
import { postAPI } from '~/lib/api';
import { API_PATHS } from '@habity/constants';
import { useUser } from '~/hooks/useUser';

interface LeadFormValues extends Record<string, unknown> {
  name?: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  zip?: string;
  topic?: string;
}

interface LeadGenerationFormProps {
  leadType?: 'buyer' | 'seller';
  leadSourceId?: string;
  assignmentSourceId?: string;
  // Optional broker ID to skip address-based matching and assign directly to this broker
  brokerId?: number;
  // Name of the form (human-readable, for logging)
  formName?: string;

  // Field selectors (name, email, phone are always visible)
  showStreetField?: boolean;
  showCityField?: boolean;
  showZipField?: boolean;
  showTopicField?: boolean;

  // Labels and placeholders
  nameLabel?: string;
  namePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  phoneLabel?: string;
  phonePlaceholder?: string;
  streetLabel?: string;
  streetPlaceholder?: string;
  cityLabel?: string;
  cityPlaceholder?: string;
  zipLabel?: string;
  zipPlaceholder?: string;
  topicLabel?: string;
  topicPlaceholder?: string;
  valuationTopicLabel?: string;
  meetingTopicLabel?: string;
  claimTopicLabel?: string;
  submitLabel?: string;
}

export function LeadGenerationForm({
  leadType = 'buyer',
  leadSourceId,
  assignmentSourceId,
  brokerId,
  formName,

  // Field selectors (name, email, phone are always visible)
  showStreetField = false,
  showCityField = false,
  showZipField = false,
  showTopicField = false,

  // Labels and placeholders
  nameLabel = 'Full Name',
  namePlaceholder = 'Enter your full name (optional)',
  emailLabel = 'Email Address',
  emailPlaceholder = 'Enter your email address (optional)',
  phoneLabel = 'Phone Number',
  phonePlaceholder = 'Enter your phone number (optional)',
  streetLabel = 'Street Address',
  streetPlaceholder = 'Enter your street address (optional)',
  cityLabel = 'City',
  cityPlaceholder = 'Enter your city (optional)',
  zipLabel = 'Zip Code',
  zipPlaceholder = 'Enter your zip code (optional)',
  topicLabel = 'Topic',
  topicPlaceholder = 'Select topic...',
  valuationTopicLabel = 'Property Valuation',
  meetingTopicLabel = 'Schedule Meeting',
  claimTopicLabel = 'Claim Property',
  submitLabel = 'Send Lead',
}: LeadGenerationFormProps) {
  const toast = useToast();
  const { user } = useUser();
  const pathname = usePathname();

  // Require leadSourceId to be configured in Plasmic
  if (!leadSourceId) {
    console.error(
      'LeadGenerationForm: leadSourceId is required but not configured in Plasmic'
    );
    return null;
  }

  // For seller leads, assignmentSourceId is required if the lead source is AcceptObjectFlow
  if (leadType === 'seller' && !assignmentSourceId) {
    console.error(
      'LeadGenerationForm: assignmentSourceId is required for seller leads in Plasmic'
    );
    return null;
  }

  // formPath is the page URL where the form was submitted
  const formPath = pathname || undefined;

  const defaultName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '';
  const defaultEmail = user?.email || '';
  const defaultPhone = (() => {
    const raw = user?.phone;
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.value) return parsed.value;
    } catch {
      // Not JSON, use as-is
    }
    return raw;
  })();

  const form = useForm<LeadFormValues>({
    defaultValues: {
      name: defaultName,
      email: defaultEmail,
      phone: defaultPhone,
      street: '',
      city: '',
      zip: '',
      topic: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (values: LeadFormValues) => {
    const { name, email, phone, street, city, zip, topic } = values;

    if (!email) {
      toast({
        title: 'Email krävs',
        description: 'Vänligen ange din e-postadress',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    // Split name into first and last name
    const nameParts = (name || '').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const action =
      leadType === 'seller'
        ? 'entered_seller_lead_form'
        : 'entered_buyer_lead_form';

    try {
      const data = await postAPI(
        API_PATHS.PROVIDERS.SUPABASE.USERS.ACTION,
        JSON.stringify({
          email,
          action,
          userData: {
            first_name: firstName,
            last_name: lastName,
            name: (name || '').trim(),
            phone,
            address_street: street,
            address_city: city,
            address_zip: zip,
            address_country: 'SE',
          },
          leadSourceId,
          assignmentSourceId,
          topic,
          leadFormBrokerId: brokerId,
          formName: formName || undefined,
          formPath,
        })
      );

      if (data?.error || !data?.user) {
        toast({
          title: 'Ett fel uppstod',
          description: data?.message || 'Försök igen senare',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      toast({
        title: 'Tack för din förfrågan!',
        description: 'Vi kommer att kontakta dig inom kort.',
        status: 'success',
        duration: 5000,
      });

      form.reset();
    } catch (error) {
      toast({
        title: 'Ett fel uppstod',
        description: 'Försök igen senare',
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} p={6} w="full">
      <Stack spacing={4} w="full" align="stretch">
        {/* Name - always visible */}
        <FormControl>
          <FormLabel htmlFor="name">{nameLabel}</FormLabel>
          <Input
            id="name"
            placeholder={namePlaceholder}
            {...register('name')}
          />
          <FormErrorMessage>{errors.name?.message as string}</FormErrorMessage>
        </FormControl>

        {/* Email - always visible */}
        <FormControl>
          <FormLabel htmlFor="email">{emailLabel}</FormLabel>
          <Input
            id="email"
            type="text"
            placeholder={emailPlaceholder}
            {...register('email', {
              validate: (value) =>
                !value ||
                value === '' ||
                /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) ||
                'Invalid email address',
            })}
          />
          <FormErrorMessage>{errors.email?.message as string}</FormErrorMessage>
        </FormControl>

        {/* Phone - always visible */}
        <FormControl>
          <FormLabel htmlFor="phone">{phoneLabel}</FormLabel>
          <Input
            id="phone"
            type="text"
            placeholder={phonePlaceholder}
            {...register('phone', {
              validate: (value) =>
                !value ||
                value === '' ||
                /^[0-9+\-\s()]{7,}$/.test(value) ||
                'Invalid phone number',
            })}
          />
          <FormErrorMessage>{errors.phone?.message as string}</FormErrorMessage>
        </FormControl>

        {/* Street */}
        {showStreetField && (
          <FormControl>
            <FormLabel htmlFor="street">{streetLabel}</FormLabel>
            <Input
              id="street"
              type="text"
              placeholder={streetPlaceholder}
              {...register('street')}
            />
            <FormErrorMessage>
              {errors.street?.message as string}
            </FormErrorMessage>
          </FormControl>
        )}

        {/* City */}
        {showCityField && (
          <FormControl>
            <FormLabel htmlFor="city">{cityLabel}</FormLabel>
            <Input
              id="city"
              type="text"
              placeholder={cityPlaceholder}
              {...register('city')}
            />
            <FormErrorMessage>
              {errors.city?.message as string}
            </FormErrorMessage>
          </FormControl>
        )}

        {/* Zip */}
        {showZipField && (
          <FormControl>
            <FormLabel htmlFor="zip">{zipLabel}</FormLabel>
            <Input
              id="zip"
              type="text"
              placeholder={zipPlaceholder}
              {...register('zip')}
            />
            <FormErrorMessage>{errors.zip?.message as string}</FormErrorMessage>
          </FormControl>
        )}

        {/* Topic */}
        {showTopicField && (
          <FormControl>
            <FormLabel htmlFor="topic">{topicLabel}</FormLabel>
            <Select
              id="topic"
              placeholder={topicPlaceholder}
              {...register('topic')}
            >
              <option value="valuation">{valuationTopicLabel}</option>
              <option value="meeting">{meetingTopicLabel}</option>
              <option value="claim">{claimTopicLabel}</option>
            </Select>
            <FormErrorMessage>
              {errors.topic?.message as string}
            </FormErrorMessage>
          </FormControl>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          colorScheme="blue"
          isLoading={isSubmitting}
          w="full"
        >
          {submitLabel}
        </Button>
      </Stack>
    </Box>
  );
}
